/**
 * Reparto de anchos de las vistas de la mesa de ayuda en escritorio: una lista a
 * la izquierda, el chat al centro y una columna lateral a la derecha (el
 * contacto en /admin/helpdesk, el panel de incidencias en la vista de atención).
 *
 * Funciones puras y parametrizadas por límites para que las dos vistas usen la
 * MISMA regla con sus propias medidas.
 *
 * @typedef {object} ColumnLimits
 * @property {number} listMin  ancho mínimo de la lista.
 * @property {number} listMax  ancho máximo de la lista.
 * @property {number} sideMin  ancho mínimo de la columna lateral.
 * @property {number} sideMax  ancho máximo de la columna lateral.
 * @property {number} chatMin  ancho que los divisores le protegen al chat. No es
 *   una columna del grid: es la invariante que impide que la conversación quede
 *   aplastada entre la lista y la columna lateral.
 */

/**
 * Ajusta los anchos guardados al espacio realmente disponible. Se usa SOLO para
 * renderizar: los valores persistidos no se tocan, para que volver a una
 * pantalla grande recupere la preferencia original intacta.
 *
 * @param {{containerWidth: number, listWidth: number, sideWidth: number, sideVisible: boolean}} widths
 *   containerWidth es el ancho medido del grid (0 o ausente = todavía sin medir).
 * @param {ColumnLimits} limits
 * @returns {{list: number, side: number, maxList: number, maxSide: number}}
 */
export function clampWidths({ containerWidth, listWidth, sideWidth, sideVisible }, limits) {
  const { listMin, listMax, sideMin, sideMax, chatMin } = limits;
  const bounded = (value, min, max) => Math.min(Math.max(value, min), max);
  let side = bounded(sideWidth, sideMin, sideMax);
  let list = bounded(listWidth, listMin, listMax);

  // Sin medida del contenedor todavía (primer render) solo aplican los límites
  // duros: repartir contra un ancho 0 colapsaría todo a los mínimos.
  if (!containerWidth || containerWidth <= 0) {
    return { list, side, maxList: listMax, maxSide: sideMax };
  }

  const reserved = sideVisible ? side : 0;
  const maxList = bounded(containerWidth - reserved - chatMin, listMin, listMax);
  list = Math.min(list, maxList);
  const maxSide = sideVisible
    ? bounded(containerWidth - list - chatMin, sideMin, sideMax)
    : sideMax;
  side = Math.min(side, maxSide);

  return { list, side, maxList, maxSide };
}

/**
 * ¿Entra la columna lateral acoplada junto a la lista y al chat, todos en sus
 * mínimos? El piso del chat para acoplar (chatDockMin) va por debajo de chatMin
 * a propósito: es lo mínimo legible, no lo que protegen los divisores. Sin
 * medida todavía se asume que entra, igual que en clampWidths.
 *
 * @param {number} containerWidth
 * @param {{listMin: number, chatDockMin: number, sideMin: number}} limits
 * @returns {boolean}
 */
export function sidePanelFits(containerWidth, { listMin, chatDockMin, sideMin }) {
  return !containerWidth || containerWidth >= listMin + chatDockMin + sideMin;
}
